import { Injectable, inject, signal } from '@angular/core';
import { PlayExecutionReportingData, ChartResultElement, ChartSeries } from '@r1-compass-library/models';
import { PlayExecutionReportingService } from '@r1-compass-library/services/reporting/play-execution-reporting.service';
import { PlayExecutionTableService } from '@r1-compass-library/services/reporting/play-execution-table.service';
import { PlayExecutionTableRecord } from '@r1-compass-library/components/reporting/report-table/play-execution-table-record.model';
import {
    PlayExecutionInteractionErrorTableData,
    PlayExecutionInteractionErrorTableService,
} from '@r1-compass-library/services/reporting/play-execution-interaction-error-table.service';

/**
 * A service used for retrieving and formatting reporting data for the play-reporting section.
 */
@Injectable({
    providedIn: 'root',
})
export class PlayReportingService {
    readonly #playExecutionReportingService = inject(PlayExecutionReportingService);
    readonly #playExecutionTableService = inject(PlayExecutionTableService);
    readonly #playExecutionInteractionErrorTableService = inject(PlayExecutionInteractionErrorTableService);

    readonly chartData = signal<ChartResultElement[]>([]);
    readonly tableRecords = signal<PlayExecutionTableRecord[] | null>(null);
    readonly interactionErrorTableData = signal<PlayExecutionInteractionErrorTableData | null>(null);

    appSpecificColumns = this.#playExecutionTableService.appSpecificColumns;
    appSpecificDetails = this.#playExecutionTableService.appSpecificDetails;

    getReportingData(playId: number, startRangeIsoString: string, endRangeIsoString: string) {
        this.chartData.set([]);
        this.tableRecords.set(null);
        this.#playExecutionReportingService
            .getExecutionReportingData(playId, startRangeIsoString, endRangeIsoString)
            .subscribe(data => {
                this.chartData.set(this.transformToChartData(data));
                this.tableRecords.set(this.#playExecutionTableService.getExecutionTableData(data));
            });
    }

    getInteractionErrorTableData(playResultId: number, executionReportData: PlayExecutionTableRecord[]) {
        this.interactionErrorTableData.set(null);
        this.#playExecutionInteractionErrorTableService
            .getInteractionErrorTableData(playResultId, executionReportData)
            .subscribe(data => this.interactionErrorTableData.set(data));
    }

    private transformToChartData(reportData: PlayExecutionReportingData[]): ChartResultElement[] {
        const chartDataArr: ChartResultElement[] = [];
        reportData.forEach(dataEntry => {
            const newDataChartObj = new ChartResultElement();
            newDataChartObj.name = dataEntry.startDate.toLocaleString();
            newDataChartObj.series = this.createNewChartSeries(dataEntry);
            chartDataArr.push(newDataChartObj);
        });
        return chartDataArr;
    }

    private createNewChartSeries(entry: PlayExecutionReportingData): ChartSeries[] {
        const newChartSeriesArr: ChartSeries[] = [
            {
                name: 'Created',
                value: entry.numRecordsCreated || 0,
            },
            {
                name: 'Updated',
                value: entry.numRecordsUpdated || 0,
            },
            {
                name: 'Failed',
                value: entry.numRecordsFailed || 0,
            },
            {
                name: 'Rejected',
                value: entry.numRecordsRejected || 0,
            },
        ];
        return newChartSeriesArr;
    }
}
